import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { userPerms } from "@/lib/permissions";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import {
  GaugeIcon,
  CarIcon,
  PlusCircleIcon,
  FileCodeIcon,
  ListChecksIcon,
  TagIcon,
  UsersIcon,
  GearSixIcon,
  SignOutIcon,
  BookmarkSimpleIcon,
  HandshakeIcon,
  TrashIcon,
  KeyIcon,
  BookOpenIcon,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const navItems = [
  { to: "/", label: "Πίνακας", icon: GaugeIcon, end: true, testid: "nav-dashboard", perm: "dashboard" },
  { to: "/inventory", label: "Στόλος", icon: CarIcon, testid: "nav-inventory", perm: "inventory" },
  { to: "/cars/new", label: "Νέο Αυτοκίνητο", icon: PlusCircleIcon, testid: "nav-add-car", perm: "add_car" },
  { to: "/bookmarklet", label: "Εισαγωγή car.gr", icon: BookmarkSimpleIcon, testid: "nav-bookmarklet", perm: "import_cargr" },
  { to: "/sell-leads", label: "Αιτήσεις Πώλησης", icon: HandshakeIcon, testid: "nav-sell-leads", perm: "sell_leads" },
  { to: "/trash", label: "Διαγραμμένα", icon: TrashIcon, testid: "nav-trash", perm: "trash" },
  { to: "/users", label: "Χρήστες", icon: UsersIcon, testid: "nav-users", adminOnly: true },
  { to: "/exports", label: "Εξαγωγές", icon: FileCodeIcon, testid: "nav-exports", perm: "exports" },
  { to: "/settings", label: "Ρυθμίσεις", icon: GearSixIcon, testid: "nav-settings", perm: "settings" },
];

export default function Layout() {
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { lang, setLang } = useI18n();
  const [pwOpen, setPwOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);

  const downloadManual = async () => {
    setManualBusy(true);
    try {
      const r = await api.get("/manual/salesperson.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "BESIKOS-CARS-Manual.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Αποτυχία λήψης εγχειριδίου");
    } finally {
      setManualBusy(false);
    }
  };
  const perms = userPerms(user);
  const items = navItems.filter((n) => {
    if (n.adminOnly && user?.role !== "admin") return false;
    if (n.perm && !perms.includes(n.perm)) return false;
    return true;
  });
  // Salesperson lands on /inventory by default (Dashboard is hidden for them)
  const fallback = items[0] || { to: "/inventory" };
  const current =
    items.find((n) =>
      n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)
    ) || fallback;

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className="w-[260px] border-r border-black/10 min-h-screen flex flex-col sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-black/10">
          <div className="flex items-baseline gap-1">
            <span className="heading text-xl font-extrabold tracking-tight leading-none">BESIKOS</span>
            <span className="heading text-xl font-light tracking-tight leading-none">CARS</span>
          </div>
        </div>

        <nav className="flex-1 py-4">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm transition-colors duration-150 border-l-2 ${
                    isActive
                      ? "border-[#E60000] bg-[#F9FAFB] text-[#0A0A0A] font-medium"
                      : "border-transparent text-[#4B5563] hover:bg-[#F9FAFB] hover:text-[#0A0A0A]"
                  }`
                }
              >
                <Icon size={20} weight="regular" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-6 py-4 border-t border-black/10">
          <div className="flex items-center gap-1 mb-3" data-testid="language-switcher">
            <button
              type="button"
              onClick={() => setLang("el")}
              className={`flex-1 text-[10px] uppercase tracking-wider py-1.5 border ${
                lang === "el"
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-[#4B5563] border-black/15 hover:border-black/40"
              }`}
              data-testid="lang-el-btn"
              title="Ελληνικά"
            >
              EL
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`flex-1 text-[10px] uppercase tracking-wider py-1.5 border ${
                lang === "en"
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-[#4B5563] border-black/15 hover:border-black/40"
              }`}
              data-testid="lang-en-btn"
              title="English"
            >
              EN
            </button>
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || "—"}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-mono">{user?.role}</div>
            </div>
            <div className="flex items-center">
              <button onClick={() => setPwOpen(true)} className="p-2 hover:bg-black/5" title="Αλλαγή κωδικού" data-testid="change-password-btn">
                <KeyIcon size={16} />
              </button>
              <button onClick={doLogout} className="p-2 hover:bg-black/5" title="Αποσύνδεση" data-testid="logout-btn">
                <SignOutIcon size={16} />
              </button>
            </div>
          </div>
        </div>
      </aside>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      {/* Floating manual download — bottom right corner */}
      <button
        onClick={downloadManual}
        disabled={manualBusy}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-[#0A0A0A] text-white pl-3 pr-4 py-2.5 shadow-lg hover:bg-[#E60000] transition-colors duration-150 disabled:opacity-60"
        title="Κατεβάστε το εγχειρίδιο χρήσης της πλατφόρμας (PDF για εκτύπωση)"
        data-testid="download-manual-btn"
      >
        <BookOpenIcon size={18} weight="regular" />
        <span className="text-xs font-medium tracking-wide">
          {manualBusy ? "Λήψη…" : "Εγχειρίδιο (PDF)"}
        </span>
      </button>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="border-b border-black/10 bg-white sticky top-0 z-10">
          <div className="px-8 py-5 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">
                Ενότητα
              </div>
              <h1 className="heading text-2xl font-extrabold tracking-tight mt-0.5">
                {current.label}
              </h1>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">
                Σημερινή Ημερομηνία
              </div>
              <div className="font-mono text-sm mt-0.5" data-testid="current-date">
                {new Date().toLocaleDateString("el-GR")}
              </div>
            </div>
          </div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
